import React, {useState, useEffect} from 'react';
import DraftEditor from './DraftEditor';
import DraftPreview from './DraftPreview';
import { Modal } from './Modal';
import {draftService} from '../../services/draftService';
import {PHARMACY_IDS} from '../../config/api';
import {transformOrderToDraft} from "../../utils/transformers";
import { useToast } from '../../context/ToastContext';
import { getApiErrorMessage, useApiSessionRestored } from '../../services/internalApi';

function apiErrorMessage(e) {
    return getApiErrorMessage(e, 'Something went wrong');
}

const FETCH_CANCELLED = 'FETCH_CANCELLED';

async function fetchOrderId(order_id, { onSplitDeliveryConfirm, onCancelledOrderConfirm }) {
    const id = typeof order_id === 'string' ? order_id.trim() : '';
    if (!id) {
        throw new Error('Enter a CE order ID.');
    }
    try {
        const orderData = await draftService.fetchOrderDataById(id);
        let newDraft;
        const isCancelled = orderData.order.bill_status === 'cancelled';
        const isBilledSplitEligible =
            orderData.order.cart.cart_status === 'BILLED' &&
            (!orderData.order.cart.channel ||
                orderData.order.cart.channel === 'APP' ||
                orderData.order.cart.channel === 'WHATSAPP');

        // Cancelled orders: choose split delivery or a full duplicate (order_id cleared)
        if (isCancelled) {
            const action = await onCancelledOrderConfirm();
            if (!action) {
                throw new Error(FETCH_CANCELLED);
            }
            if (action === 'split') {
                newDraft = transformOrderToDraft(orderData, false, true);
            } else {
                // Duplicate: copy items/charges, keep channel, clear order_id
                newDraft = transformOrderToDraft(orderData, true, false, true);
            }
        } else if (isBilledSplitEligible) {
            const ok = await onSplitDeliveryConfirm();
            if (!ok) {
                throw new Error(FETCH_CANCELLED);
            }
            newDraft = transformOrderToDraft(orderData, false, true);
        } else if (orderData.order.cart.cart_status !== 'BILLED') {
            newDraft = transformOrderToDraft(orderData, true, false);
        } else {
            throw new Error('Unable to pull this billed order.');
        }
        await draftService.createDraft(newDraft);
        window.location.reload();
    } catch (e) {
        if (e instanceof Error && e.message === 'Unable to pull this billed order.') {
            throw e;
        }
        if (e instanceof Error && e.message === FETCH_CANCELLED) {
            throw e;
        }
        throw new Error(apiErrorMessage(e));
    }
}

async function createDraftFromPhone(phone_number) {
    const digits = String(phone_number || '').replace(/\D/g, '').slice(-10);
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(digits)) {
        throw new Error('Enter a valid 10-digit mobile number.');
    }
    try {
        await draftService.createDraft({
            customer: {
                name: '',
                mobile: `+91${digits}`,
                address_details: {
                    address: '',
                    pincode: '',
                    latitude: '',
                    longitude: ''
                }
            },
            items: [],
            additionalCharges: [],
            status: 'pending',
            createdAt: new Date().toISOString(),
            pharmacy: '4',
            partner: '',
            customPartner: '',
            pocName: '',
            doctor: '',
        });
        window.location.reload();
    } catch (e) {
        throw new Error(apiErrorMessage(e));
    }
}

function AdminDashboard() {
    const toast = useToast();
    const apiSessionRestored = useApiSessionRestored();
    const [drafts, setDrafts] = useState([]);
    const [selectedDraft, setSelectedDraft] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isViewing, setIsViewing] = useState(false);

    /** Split-delivery confirm: `resolve(true)` to continue, `false` to cancel */
    const [splitDeliveryModal, setSplitDeliveryModal] = useState(null);
    /** Cancelled-order choice: `resolve('split'|'duplicate')` or `false` to cancel */
    const [cancelledOrderModal, setCancelledOrderModal] = useState(null);

    const [ceModalOpen, setCeModalOpen] = useState(false);
    const [ceOrderId, setCeOrderId] = useState('');
    const [ceSubmitting, setCeSubmitting] = useState(false);
    const [ceError, setCeError] = useState(null);

    const [phoneModalOpen, setPhoneModalOpen] = useState(false);
    const [phoneValue, setPhoneValue] = useState('');
    const [phoneSubmitting, setPhoneSubmitting] = useState(false);
    const [phoneError, setPhoneError] = useState(null);

    /** `{ id, name, mobile }` when confirming delete */
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const openCeModal = () => {
        setCeOrderId('');
        setCeError(null);
        setCeModalOpen(true);
    };

    const openPhoneModal = () => {
        setPhoneValue('');
        setPhoneError(null);
        setPhoneModalOpen(true);
    };

    const handleCeSubmit = async (e) => {
        e?.preventDefault?.();
        setCeSubmitting(true);
        setCeError(null);
        try {
            await fetchOrderId(ceOrderId, {
                onSplitDeliveryConfirm: () => new Promise((resolve) => {
                    setSplitDeliveryModal({ resolve });
                }),
                onCancelledOrderConfirm: () => new Promise((resolve) => {
                    setCancelledOrderModal({ resolve });
                }),
            });
        } catch (err) {
            if (err instanceof Error && err.message === FETCH_CANCELLED) {
                toast.info('Draft creation cancelled.');
                return;
            }
            setCeError(err instanceof Error ? err.message : 'Failed to fetch order');
        } finally {
            setCeSubmitting(false);
        }
    };

    const closeSplitDeliveryModal = (confirmed) => {
        if (splitDeliveryModal) {
            splitDeliveryModal.resolve(Boolean(confirmed));
            setSplitDeliveryModal(null);
        }
    };

    const closeCancelledOrderModal = (action) => {
        if (cancelledOrderModal) {
            cancelledOrderModal.resolve(action || false);
            setCancelledOrderModal(null);
        }
    };

    const handlePhoneSubmit = async (e) => {
        e?.preventDefault?.();
        setPhoneSubmitting(true);
        setPhoneError(null);
        try {
            await createDraftFromPhone(phoneValue);
        } catch (err) {
            setPhoneError(err instanceof Error ? err.message : 'Failed to create draft');
        } finally {
            setPhoneSubmitting(false);
        }
    };

    useEffect(() => {
        loadDrafts();
    }, [apiSessionRestored]);

    const loadDrafts = async () => {
        try {
            const response = await draftService.getAllDrafts({
                page: 1,
                limit: 20
            });
            setDrafts(response.data.map(draft => ({
                ...draft,
                _id: draft._id || draft.id
            })));
        } catch (error) {
            console.error('Error loading drafts:', error);
        }
    };

    const handleDraftClick = (draft) => {
        setSelectedDraft(draft);
        setIsViewing(true);
    };

    const handleEditClick = (draft) => {
        const draftId = draft._id || draft.id;

        if (!draftId) {
            console.error('Draft ID is missing');
            toast.error('Cannot edit draft: ID is missing.');
            return;
        }

        const formattedDraft = {
            ...draft,
            _id: draftId,
            items: draft.items.map(item => ({
                medicine: {
                    id: item.medicine?.id || item.medicine_id,
                    name: item.medicine?.name || item.medicine_name,
                    manufacturer: item.medicine?.manufacturer || item.manufacturer,
                    content: item.medicine?.content || item.content,
                    size: item.medicine?.size || item.size,
                    price: item.medicine?.price || item.price,
                    batch: item.medicine?.batch || item.batch || 'Pending',
                    expiry: item.medicine?.expiry || item.expiry || '',
                    packing_size: item.medicine?.packing_size || item.packing_size || '',
                    discount_tier: item.medicine?.discount_tier || item.discount_tier || ''
                },
                quantity: item.quantity || 0,
                discount: item.discount || 0
            })),
            partner: draft.partner || '',
            customPartner: draft.custom_partner || draft.customPartner || '',
            pocName: draft.poc_name || draft.pocName || '',
            doctor: draft.doctor || '',
            additionalCharges: (draft.additional_charges || draft.additionalCharges || []).map(charge => ({
                id: charge.id || Date.now() + Math.random(),
                type: charge.charge_type || charge.type,
                name: charge.name,
                amount: parseFloat(charge.amount) || 0
            }))
        };
        
        setSelectedDraft(formattedDraft);
        setIsEditing(true);
    };

    const handleDraftClose = () => {
        setSelectedDraft(null);
        setIsViewing(false);
        setIsEditing(false);
    };

    const openDeleteModal = (draft) => {
        const id = draft._id || draft.id;
        if (!id) return;
        setDeleteError(null);
        setDeleteTarget({
            id,
            name: draft.customer?.name || 'No name',
            mobile: draft.customer?.mobile || '—',
        });
    };

    const closeDeleteModal = () => {
        if (!deleteSubmitting) {
            setDeleteTarget(null);
            setDeleteError(null);
        }
    };

    const confirmDeleteDraft = async () => {
        if (!deleteTarget) return;
        setDeleteSubmitting(true);
        setDeleteError(null);
        try {
            await draftService.deleteDraft(deleteTarget.id);
            await loadDrafts();
            setDeleteTarget(null);
        } catch (error) {
            console.error('Error deleting draft:', error);
            setDeleteError(apiErrorMessage(error));
        } finally {
            setDeleteSubmitting(false);
        }
    };

    return (
        <div className="dashboard-container">
            <header className="mb-8 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-primary-600">B2B</p>
                    <h1 className="page-title">Drafts</h1>
                    <p className="page-subtitle">
                        Create and bill orders from pending drafts. Fetch from CE or start from a phone number.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={openCeModal}
                        className="btn-secondary flex items-center gap-2 text-sm"
                    >
                        <i className="material-icons-outlined text-base">download</i>
                        <span>Fetch from CE</span>
                    </button>
                    <button
                        type="button"
                        onClick={openPhoneModal}
                        className="btn-primary flex items-center gap-2 text-sm"
                    >
                        <i className="material-icons-outlined text-base">add</i>
                        <span>New draft</span>
                    </button>
                </div>
            </header>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="card-surface p-5 lg:col-span-1">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Pending</p>
                    <p className="mt-1 text-3xl font-bold tabular-nums text-slate-900">{drafts.length}</p>
                    <p className="mt-1 text-sm text-slate-500">drafts in queue</p>
                </div>
                <div className="card-surface p-6 lg:col-span-2">
                    <div className="mb-5 flex items-center justify-between gap-4">
                        <h2 className="text-lg font-semibold text-slate-800">Queue</h2>
                    </div>
                    <div className="space-y-3">
                        {drafts.length === 0 && (
                            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-6 py-12 text-center">
                                <i className="material-icons-outlined mb-2 text-4xl text-slate-300">inbox</i>
                                <p className="font-medium text-slate-600">No drafts yet</p>
                                <p className="mt-1 text-sm text-slate-500">Create a draft or fetch an order from CE.</p>
                            </div>
                        )}
                        {drafts.map(draft => (
                            <div
                                key={draft._id || draft.id}
                                className="group flex flex-col gap-4 rounded-xl border border-slate-100 bg-slate-50/50 p-4 transition-all hover:border-primary-200/60 hover:bg-white hover:shadow-md sm:flex-row sm:items-center sm:justify-between"
                            >
                                <div className="flex min-w-0 flex-1 gap-3">
                                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                                        <i className="material-icons-outlined text-xl">person</i>
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="truncate font-semibold text-slate-900">
                                            {draft.customer?.name || 'No name'}
                                        </h3>
                                        <p className="text-sm text-slate-500">{draft.customer?.mobile || 'No mobile'}</p>
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            <span className="inline-flex items-center rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200/80">
                                                {Object.keys(PHARMACY_IDS).find(key => PHARMACY_IDS[key] === draft.pharmacy)?.replace(/_/g, ' ')?.toLowerCase() || 'Pharmacy'}
                                            </span>
                                            <span className="inline-flex items-center rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200/80">
                                                {draft.items?.length || 0} items
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">
                                    <button
                                        type="button"
                                        onClick={() => handleDraftClick(draft)}
                                        className="btn-secondary px-3 py-2 text-sm"
                                    >
                                        View
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleEditClick(draft)}
                                        className="btn-primary px-3 py-2 text-sm"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => openDeleteModal(draft)}
                                        className="rounded-xl border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {isViewing && selectedDraft && (
                <DraftPreview
                    draft={selectedDraft}
                    onClose={handleDraftClose}
                />
            )}

            {isEditing && selectedDraft && (
                <DraftEditor
                    key={selectedDraft._id}
                    draft={selectedDraft}
                    onSave={draftService.saveDraft}
                    onAddShortbook={(medicine_id, order_id, strip_quantity) =>
                        draftService.addShortbook(selectedDraft._id, medicine_id, order_id, strip_quantity)
                    }
                    onCancel={handleDraftClose}
                    onBill={async ({ redeemWallet }) => {
                        await draftService.billDraft(selectedDraft._id, { redeemWallet });
                    }}
                />
            )}

            <Modal
                isOpen={ceModalOpen}
                onClose={() => {
                    if (!ceSubmitting) setCeModalOpen(false);
                }}
                title="Fetch from CE"
                description="Enter the CE / Chatwoot conversation or order ID. A new draft will be created from that order."
            >
                <form onSubmit={handleCeSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="ce-order-id" className="mb-1.5 block text-sm font-medium text-slate-700">
                            Order ID
                        </label>
                        <input
                            id="ce-order-id"
                            type="text"
                            className="input-field font-mono text-sm"
                            placeholder="e.g. 12345678"
                            value={ceOrderId}
                            onChange={(ev) => {
                                setCeOrderId(ev.target.value);
                                setCeError(null);
                            }}
                            disabled={ceSubmitting}
                            autoComplete="off"
                            autoFocus
                        />
                        {ceError && (
                            <p className="mt-2 text-sm text-red-600" role="alert">
                                {ceError}
                            </p>
                        )}
                    </div>
                    <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-5">
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => setCeModalOpen(false)}
                            disabled={ceSubmitting}
                        >
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary min-w-[7rem]" disabled={ceSubmitting}>
                            {ceSubmitting ? (
                                <span className="inline-flex items-center gap-2">
                                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                    Fetching…
                                </span>
                            ) : (
                                'Fetch draft'
                            )}
                        </button>
                    </div>
                </form>
            </Modal>

            <Modal
                isOpen={phoneModalOpen}
                onClose={() => {
                    if (!phoneSubmitting) setPhoneModalOpen(false);
                }}
                title="New draft from phone"
                description="10-digit Indian mobile number. Country code +91 is added automatically."
            >
                <form onSubmit={handlePhoneSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="draft-phone" className="mb-1.5 block text-sm font-medium text-slate-700">
                            Mobile number
                        </label>
                        <div className="flex rounded-xl border border-slate-200 bg-slate-50/80 focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-500/20">
                            <span className="flex items-center pl-3 text-sm font-medium text-slate-500">+91</span>
                            <input
                                id="draft-phone"
                                type="tel"
                                inputMode="numeric"
                                className="w-full rounded-r-xl border-0 bg-transparent py-2.5 pr-3 pl-1 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
                                placeholder="9876543210"
                                value={phoneValue}
                                onChange={(ev) => {
                                    setPhoneValue(ev.target.value.replace(/\D/g, '').slice(0, 10));
                                    setPhoneError(null);
                                }}
                                disabled={phoneSubmitting}
                                autoComplete="tel-national"
                                autoFocus
                            />
                        </div>
                        {phoneError && (
                            <p className="mt-2 text-sm text-red-600" role="alert">
                                {phoneError}
                            </p>
                        )}
                    </div>
                    <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-5">
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => setPhoneModalOpen(false)}
                            disabled={phoneSubmitting}
                        >
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary min-w-[7rem]" disabled={phoneSubmitting}>
                            {phoneSubmitting ? (
                                <span className="inline-flex items-center gap-2">
                                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                    Creating…
                                </span>
                            ) : (
                                'Create draft'
                            )}
                        </button>
                    </div>
                </form>
            </Modal>

            <Modal
                isOpen={Boolean(deleteTarget)}
                onClose={closeDeleteModal}
                title="Delete this draft?"
                description={
                    deleteTarget
                        ? `This removes the draft for ${deleteTarget.name} (${deleteTarget.mobile}). You can’t undo this.`
                        : ''
                }
            >
                <div className="space-y-4">
                    {deleteError && (
                        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
                            {deleteError}
                        </p>
                    )}
                    <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-5">
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={closeDeleteModal}
                            disabled={deleteSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="inline-flex min-w-[7rem] items-center justify-center rounded-xl bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-red-700 active:scale-[0.98] disabled:opacity-60"
                            onClick={confirmDeleteDraft}
                            disabled={deleteSubmitting}
                        >
                            {deleteSubmitting ? (
                                <span className="inline-flex items-center gap-2">
                                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                    Deleting…
                                </span>
                            ) : (
                                'Delete draft'
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={Boolean(splitDeliveryModal)}
                onClose={() => closeSplitDeliveryModal(false)}
                title="Order already billed"
                description="This order is already billed. Create a draft from it for split delivery?"
            >
                <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-5">
                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => closeSplitDeliveryModal(false)}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => closeSplitDeliveryModal(true)}
                    >
                        Create draft
                    </button>
                </div>
            </Modal>

            <Modal
                isOpen={Boolean(cancelledOrderModal)}
                onClose={() => closeCancelledOrderModal(false)}
                title="Order cancelled"
                description="This order is cancelled. Create a split-delivery draft, or duplicate the order into a new draft?"
            >
                <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-5">
                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => closeCancelledOrderModal(false)}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => closeCancelledOrderModal('duplicate')}
                    >
                        Duplicate order
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => closeCancelledOrderModal('split')}
                    >
                        Split delivery
                    </button>
                </div>
            </Modal>
        </div>
    );
}

export default AdminDashboard; 